// Admin Support Email Console — Microsoft 365 + Claude reply helper.
// Mounted at /admin/support-email. Master-admin only.
import React, { useEffect, useMemo, useState } from "react";
import {
  Inbox, Send, FileEdit, Bot, Cog, Activity, Loader2, RefreshCw,
  Mail, CheckCircle2, XCircle, AlertCircle, Sparkles, Trash2,
} from "lucide-react";
import api from "@/lib/api";

const TABS = [
  { id: "inbox",    label: "Inbox",   icon: Inbox },
  { id: "drafts",   label: "Drafts",  icon: FileEdit },
  { id: "claude",   label: "Claude",  icon: Bot },
  { id: "settings", label: "Settings",icon: Cog },
  { id: "audit",    label: "Audit",   icon: Activity },
];

const CLAUDE_KINDS = [
  ["customer_support",   "Customer support reply"],
  ["affiliate_support",  "Affiliate support reply"],
  ["partner_outreach",   "Partner outreach"],
  ["driver_support",     "Driver support reply"],
  ["franchise_followup", "Franchise discovery follow-up"],
  ["general",            "General"],
];

export default function AdminSupportEmail() {
  const [tab, setTab] = useState("inbox");
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="admin-support-email">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="eyebrow text-lifted-dim">Master Admin</div>
          <h1 className="font-anton text-4xl uppercase text-white">Support Email</h1>
          <p className="text-lifted-dim text-sm mt-1 max-w-2xl">
            Microsoft 365 mailbox for <code>support@liftedatthemoment.com</code> — connected directly
            inside the dashboard. Claude can draft, rewrite, and summarize. You always send manually.
          </p>
        </div>
        <nav className="flex gap-2 flex-wrap" data-testid="support-email-tabs">
          {TABS.map(t => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              data-testid={`support-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs uppercase tracking-[0.16em] font-bebas border transition-colors
                ${tab === t.id ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40"
                                : "bg-black/30 text-lifted-dim border-white/10 hover:text-white"}`}>
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </nav>
      </header>

      {tab === "inbox" && <InboxTab />}
      {tab === "drafts" && <DraftsTab />}
      {tab === "claude" && <ClaudeTab />}
      {tab === "settings" && <SettingsTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}

// ============================ INBOX ============================
function InboxTab() {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);

  async function load() {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/email/inbox?folder=INBOX&limit=50");
      setMsgs(data?.messages || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load inbox.");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="inbox-tab">
      <aside className="glass-panel p-3 lg:col-span-1 min-h-[400px]">
        <div className="flex items-center justify-between mb-3">
          <div className="font-anton uppercase text-white text-sm">Inbox</div>
          <button onClick={load} className="text-[11px] text-lifted-dim hover:text-lifted-green inline-flex items-center gap-1" data-testid="inbox-refresh">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
        {loading && <div className="text-sm text-lifted-dim flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
        {err && <div className="text-rose-200 text-xs flex items-start gap-2 bg-rose-500/10 border border-rose-500/30 rounded-md p-2"><AlertCircle size={14} /> {err}</div>}
        {!loading && !err && msgs.length === 0 && <div className="text-sm text-lifted-dim" data-testid="inbox-empty">No messages.</div>}
        <ul className="divide-y divide-white/5">
          {msgs.map(m => (
            <li key={m.uid}>
              <button type="button" onClick={() => setSelected(m)}
                data-testid={`inbox-row-${m.uid}`}
                className={`w-full text-left py-2 px-1 hover:bg-white/5 transition-colors ${selected?.uid === m.uid ? "bg-white/5" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm truncate ${m.status === "unread" ? "text-white font-medium" : "text-lifted-dim"}`}>
                    {m.from_name || m.from_email}
                  </span>
                  {m.status === "unread" && <span className="w-1.5 h-1.5 rounded-full bg-lifted-green flex-shrink-0" />}
                </div>
                <div className="text-xs text-white truncate mt-0.5">{m.subject}</div>
                <div className="text-[10px] text-lifted-dim mt-0.5">{m.received_at?.slice(0, 16)}</div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="lg:col-span-2">
        {selected ? <MessageDetail uid={selected.uid} folder="INBOX" onAfterSend={load} /> : (
          <div className="glass-panel p-8 text-center text-lifted-dim text-sm" data-testid="inbox-no-selection">
            <Mail className="mx-auto h-8 w-8 mb-2 text-lifted-green/40" />
            Select a message to read and reply.
          </div>
        )}
      </section>
    </div>
  );
}

function MessageDetail({ uid, folder, onAfterSend }) {
  const [msg, setMsg] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [summary, setSummary] = useState("");

  useEffect(() => {
    setMsg(null); setError(""); setOkMsg(""); setReply(""); setSummary("");
    (async () => {
      try { const { data } = await api.get(`/admin/email/message/${uid}?folder=${encodeURIComponent(folder)}`); setMsg(data); }
      catch (e) { setError(e?.response?.data?.detail || "Could not load message."); }
    })();
  }, [uid, folder]);

  async function send() {
    if (!reply.trim()) return;
    setBusy(true); setError(""); setOkMsg("");
    try {
      await api.post("/admin/email/reply", { folder, uid, body_text: reply });
      setOkMsg("Reply sent.");
      setReply("");
      onAfterSend?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not send reply.");
    } finally { setBusy(false); }
  }
  async function aiDraft() {
    setAiBusy(true); setError("");
    try {
      const { data } = await api.post("/admin/email/claude-draft", {
        kind: "customer_support",
        brief: `Draft a reply to the email below. Be concise and on-brand.`,
        original_thread: msg ? `${msg.subject}\n\n${msg.body_text || msg.body_html || ""}` : "",
        customer_name: msg?.from_name || "",
      });
      setReply((r) => (r ? r + "\n\n" : "") + (data?.draft || ""));
    } catch (e) { setError(e?.response?.data?.detail || "Claude draft failed."); }
    finally { setAiBusy(false); }
  }
  async function aiRewrite() {
    if (!reply.trim()) return;
    setAiBusy(true); setError("");
    try {
      const { data } = await api.post("/admin/email/claude-rewrite", { style: "warmer", original: reply });
      setReply(data?.rewrite || reply);
    } catch (e) { setError(e?.response?.data?.detail || "Claude rewrite failed."); }
    finally { setAiBusy(false); }
  }
  async function aiSummary() {
    setAiBusy(true); setError(""); setSummary("");
    try {
      const text = msg ? `Subject: ${msg.subject}\nFrom: ${msg.from_email}\n\n${msg.body_text || msg.body_html || ""}` : "";
      const { data } = await api.post("/admin/email/claude-summary", { thread_text: text });
      setSummary(data?.summary || "");
    } catch (e) { setError(e?.response?.data?.detail || "Claude summary failed."); }
    finally { setAiBusy(false); }
  }
  async function saveDraft() {
    if (!reply.trim() || !msg) return;
    setBusy(true); setError(""); setOkMsg("");
    try {
      await api.post("/admin/email/draft", {
        to_email: msg.from_email,
        subject: msg.subject?.toLowerCase()?.startsWith("re:") ? msg.subject : `Re: ${msg.subject || ""}`,
        body: reply,
        related_message_id: msg.message_id,
        created_by_claude: false,
      });
      setOkMsg("Saved to drafts.");
    } catch (e) { setError(e?.response?.data?.detail || "Could not save draft."); }
    finally { setBusy(false); }
  }

  if (!msg && !error) return <div className="glass-panel p-6 text-sm text-lifted-dim flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  if (error && !msg) return <div className="glass-panel p-6 text-rose-200 text-sm">{error}</div>;

  return (
    <div className="glass-panel p-5 space-y-4" data-testid="message-detail">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-bebas text-lifted-dim">From</div>
        <div className="text-white text-sm">
          <strong>{msg.from_name || msg.from_email}</strong> &lt;{msg.from_email}&gt;
        </div>
        <div className="font-anton text-xl text-white uppercase mt-1">{msg.subject}</div>
        <div className="text-[11px] text-lifted-dim">{msg.received_at}</div>
      </div>

      {summary && (
        <div className="text-xs whitespace-pre-wrap bg-lifted-green/5 border border-lifted-green/30 rounded-md p-3" data-testid="message-summary">
          <div className="font-bebas uppercase text-lifted-green tracking-[0.18em] mb-1">Claude summary</div>
          {summary}
        </div>
      )}

      <div className="bg-black/30 border border-white/10 rounded-md p-3 text-xs text-lifted-dim whitespace-pre-wrap max-h-72 overflow-auto" data-testid="message-body">
        {msg.body_text || msg.body_html?.replace(/<[^>]+>/g, "") || "(no content)"}
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.18em] font-bebas text-lifted-dim">Your reply</label>
        <textarea value={reply} onChange={(e) => setReply(e.target.value)}
          rows={8} placeholder="Type your reply, or use Claude to draft…"
          className="w-full mt-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-lifted-dim"
          data-testid="reply-box" />
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={aiDraft} disabled={aiBusy} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid="claude-draft-btn">
          {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Draft with Claude
        </button>
        <button onClick={aiRewrite} disabled={aiBusy || !reply.trim()} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid="claude-rewrite-btn">
          {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Bot size={12} />} Rewrite (warmer)
        </button>
        <button onClick={aiSummary} disabled={aiBusy} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid="claude-summary-btn">
          {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Activity size={12} />} Summarize thread
        </button>
        <button onClick={saveDraft} disabled={busy || !reply.trim()} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid="save-draft-btn">
          <FileEdit size={12} /> Save draft
        </button>
        <button onClick={send} disabled={busy || !reply.trim()} className="btn-lifted inline-flex items-center gap-1.5 text-xs" data-testid="send-reply-btn">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send reply
        </button>
      </div>

      {error && <div className="text-rose-200 text-xs flex items-center gap-1.5" data-testid="message-error"><XCircle size={12} /> {error}</div>}
      {okMsg && <div className="text-lifted-green text-xs flex items-center gap-1.5" data-testid="message-ok"><CheckCircle2 size={12} /> {okMsg}</div>}
    </div>
  );
}

// ============================ DRAFTS ============================
function DraftsTab() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email/drafts?status=draft");
      setDrafts(data?.drafts || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);
  async function act(id, action) {
    setBusyId(id);
    try { await api.post(`/admin/email/drafts/${id}/${action}`); await load(); }
    catch (e) { window.alert(e?.response?.data?.detail || `Could not ${action}.`); }
    finally { setBusyId(""); }
  }
  return (
    <div className="space-y-3" data-testid="drafts-tab">
      {loading && <div className="text-sm text-lifted-dim flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      {!loading && drafts.length === 0 && <div className="text-sm text-lifted-dim" data-testid="drafts-empty">No drafts.</div>}
      <ul className="space-y-3">
        {drafts.map(d => (
          <li key={d.id} className="glass-panel p-4" data-testid={`draft-${d.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bebas text-lifted-dim">To {d.to_email}</div>
                <div className="text-white text-sm font-medium">{d.subject}</div>
                <pre className="text-xs text-lifted-dim mt-2 whitespace-pre-wrap font-mono max-h-32 overflow-auto">{d.body}</pre>
                {d.created_by_claude && <div className="text-[10px] text-lifted-green mt-1">Drafted by Claude</div>}
              </div>
              <div className="flex flex-col gap-1.5">
                <button onClick={() => act(d.id, "send")} disabled={busyId === d.id}
                  className="btn-lifted text-[10px] inline-flex items-center gap-1" data-testid={`draft-send-${d.id}`}>
                  <Send size={10} /> Send
                </button>
                <button onClick={() => act(d.id, "delete")} disabled={busyId === d.id}
                  className="btn-ghost text-[10px] inline-flex items-center gap-1" data-testid={`draft-delete-${d.id}`}>
                  <Trash2 size={10} /> Delete
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================ CLAUDE HELPER ============================
function ClaudeTab() {
  const [kind, setKind] = useState("customer_support");
  const [brief, setBrief] = useState("");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function generate() {
    setBusy(true); setErr(""); setOutput("");
    try {
      const { data } = await api.post("/admin/email/claude-draft", { kind, brief });
      setOutput(data?.draft || "");
    } catch (e) { setErr(e?.response?.data?.detail || "Could not generate."); }
    finally { setBusy(false); }
  }
  return (
    <div className="space-y-4 max-w-3xl" data-testid="claude-tab">
      <div className="glass-panel p-5 space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="text-xs text-lifted-dim font-bebas uppercase tracking-[0.18em]">
            Type
            <select value={kind} onChange={e => setKind(e.target.value)}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white"
              data-testid="claude-kind">
              {CLAUDE_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>
        <textarea value={brief} onChange={e => setBrief(e.target.value)} rows={4}
          placeholder="What does this email need to do? (recipient context, key facts, desired outcome)"
          className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-lifted-dim"
          data-testid="claude-brief" />
        <button onClick={generate} disabled={busy || !brief.trim()} className="btn-lifted inline-flex items-center gap-1.5" data-testid="claude-generate">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Generate
        </button>
        {err && <div className="text-rose-200 text-xs">{err}</div>}
      </div>
      {output && (
        <div className="glass-panel p-5">
          <div className="font-bebas uppercase text-lifted-green tracking-[0.18em] text-xs mb-2">Claude draft</div>
          <pre className="whitespace-pre-wrap text-sm text-white font-sans" data-testid="claude-output">{output}</pre>
        </div>
      )}
    </div>
  );
}

// ============================ SETTINGS ============================
function SettingsTab() {
  const [s, setS] = useState(null);
  const [test, setTest] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/email/settings"); setS(data); }
      catch { setS(null); }
    })();
  }, []);
  async function runTest() {
    setBusy(true); setTest(null);
    try { const { data } = await api.post("/admin/email/test-connection"); setTest(data); }
    catch (e) { setTest({ ok: false, error: e?.response?.data?.detail || "Test failed." }); }
    finally { setBusy(false); }
  }

  if (!s) return <div className="text-sm text-lifted-dim flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="glass-panel p-5 space-y-4 max-w-2xl" data-testid="settings-tab">
      <Row label="Support address" value={s.support_email_address || "—"} testid="sett-address" />
      <Row label="Provider" value={s.provider} />
      <Row label="SMTP" value={`${s.smtp_host}:${s.smtp_port}`} testid="sett-smtp" />
      <Row label="IMAP" value={`${s.imap_host}:${s.imap_port}`} testid="sett-imap" />
      <Row label="Password configured"
            value={s.password_configured ? "✓ Yes" : "✗ NOT SET — add SUPPORT_EMAIL_PASSWORD env var"}
            tone={s.password_configured ? "ok" : "warn"} testid="sett-password" />
      <Row label="Claude helper" value={s.claude_email_helper_enabled ? "Enabled" : "Disabled (no Anthropic key)"} />
      <div className="text-[11px] text-lifted-dim font-mono pt-2 border-t border-white/5">{s.modern_auth_hint}</div>
      <button onClick={runTest} disabled={busy} className="btn-lifted inline-flex items-center gap-1.5" data-testid="test-connection-btn">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Test connection
      </button>
      {test && <ConnectionResult r={test} />}
    </div>
  );
}
function Row({ label, value, tone, testid }) {
  const cls = tone === "ok" ? "text-lifted-green" : tone === "warn" ? "text-amber-300" : "text-white";
  return (
    <div className="grid grid-cols-2 gap-3 text-sm" data-testid={testid}>
      <div className="text-lifted-dim font-bebas uppercase tracking-[0.18em] text-xs">{label}</div>
      <div className={cls}>{value}</div>
    </div>
  );
}
function ConnectionResult({ r }) {
  return (
    <div className="text-xs space-y-1 mt-2 p-3 bg-black/30 border border-white/10 rounded-md" data-testid="test-result">
      <div className={`inline-flex items-center gap-1.5 font-bebas uppercase tracking-[0.16em] ${r.ok ? "text-lifted-green" : "text-rose-200"}`}>
        {r.ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />} Connection {r.ok ? "OK" : "FAILED"}
      </div>
      <div className="text-lifted-dim">SMTP: <span className="text-white">{r.smtp || "—"}</span>{r.smtp_error && <span className="text-rose-200"> · {r.smtp_error}</span>}</div>
      <div className="text-lifted-dim">IMAP: <span className="text-white">{r.imap || "—"}</span>{r.imap_error && <span className="text-rose-200"> · {r.imap_error}</span>}</div>
      {r.error && <div className="text-rose-200">{r.error}</div>}
    </div>
  );
}

// ============================ AUDIT ============================
function AuditTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/email/audit-logs?limit=200"); setRows(data?.logs || []); }
      finally { setLoading(false); }
    })();
  }, []);
  return (
    <div data-testid="audit-tab">
      {loading && <div className="text-sm text-lifted-dim flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      {!loading && rows.length === 0 && <div className="text-sm text-lifted-dim">No email audit entries yet.</div>}
      {rows.length > 0 && (
        <table className="w-full text-xs" data-testid="audit-table">
          <thead className="text-lifted-dim uppercase tracking-[0.16em] font-bebas">
            <tr><th className="text-left px-2 py-1.5">When</th>
                <th className="text-left px-2 py-1.5">Action</th>
                <th className="text-left px-2 py-1.5">Recipient</th>
                <th className="text-left px-2 py-1.5">Subject</th>
                <th className="text-left px-2 py-1.5">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((r, i) => (
              <tr key={i} className="text-white">
                <td className="px-2 py-1.5 font-mono text-[10px]">{(r.created_at||"").slice(0,19)}</td>
                <td className="px-2 py-1.5">{r.action}</td>
                <td className="px-2 py-1.5 text-[11px]">{r.recipient_email || "—"}</td>
                <td className="px-2 py-1.5 text-[11px]">{(r.subject || "").slice(0,60)}</td>
                <td className={`px-2 py-1.5 text-[11px] ${r.status === "sent" ? "text-lifted-green" : r.status === "failed" ? "text-rose-200" : ""}`}>{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
